import styled from '@emotion/styled';
import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import Image from 'next/image';
import { forwardRef } from 'react';
import { mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import VideoPreview from './VideoPreview';
import { PrimeTrialPromoModalProps } from './types';

const PITCH_BG = '/static/images/prime-pitch-bg.svg';

const Transition = forwardRef(function Transition(
  props: Record<string, unknown>,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const COLORS = {
  surface: '#100B20',
  card: '#252038',
  purple: '#9875FF',
  white: '#FFFFFF'
} as const;

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container {
    align-items: center;
    padding: 16px;
  }
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    margin: 0;
    max-width: 500px;
    width: 100%;
    border-radius: 16px;
    background: transparent;
    box-shadow: none;
    overflow: hidden;

    ${mediaQuery.uptoMobile} {
      max-width: 336px;
      border-radius: 12px;
    }
  }
`;

const ModalSurface = styled.div`
  background: ${COLORS.surface};
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQuery.uptoMobile} {
    border-radius: 12px;
  }
`;

const Hero = styled.div`
  position: relative;
  height: 200px;
  width: 100%;
  ${mediaQuery.uptoMobile} {
    height: 128px;
  }
`;

const Logo = styled(Image)`
  position: absolute;
  margin: 24px 21px;
  z-index: 2;

  ${mediaQuery.uptoMobile} {
    margin: 16px;
  }
`;

const MidSection = styled.div<{ $hasPriceImage?: boolean }>`
  background: linear-gradient(180deg, #312357 0%, rgba(49, 35, 87, 0) 100%),
    url(${PITCH_BG});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%, 360px 260px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: fit-content;
  max-height: fit-content;
  padding-top: 11px;
  justify-content: flex-end;
  padding-top: ${({ $hasPriceImage }) => ($hasPriceImage ? '0' : '25px')};

  ${mediaQuery.uptoMobile} {
    padding-top: ${Spacings.SPACING_6B};
    height: fit-content;
    max-height: fit-content;
  }
`;

const OfferLine = styled.p`
  max-width: 400px;
  color: ${COLORS.white};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin-top: 15px;
  margin-bottom: 10px;

  ${mediaQuery.uptoMobile} {
    font-size: 18px;
    line-height: 24px;
  }
`;

const ThenLine = styled.p<{ $hasPriceImage?: boolean }>`
  color: ${COLORS.white};
  font-size: 18px;
  line-height: 18px;
  font-weight: 400;
  margin-top: ${({ $hasPriceImage }) => ($hasPriceImage ? '-20px' : '0')};

  ${mediaQuery.uptoMobile} {
    font-size: 14px;
    line-height: 14px;
  }
`;

const BoldThenLine = styled.span`
  font-weight: 700;
`;

const BenefitsRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 0 16px;
  margin: 20px 0;

  ${mediaQuery.uptoMobile} {
    gap: 7px;
    padding: 0 14px;
    margin: 16px 0;
  }
`;

const BenefitCard = styled.div`
  flex: 1;
  min-width: 0;
  background: ${COLORS.card};
  border-radius: 8px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  ${mediaQuery.uptoMobile} {
    border-radius: 7px;
    padding: 11px 7px;
    gap: 3px;
  }
`;

const BenefitTitle = styled.span`
  color: ${COLORS.white};
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  line-height: 100%;
  margin-top: 8px;

  ${mediaQuery.uptoMobile} {
    font-size: 14px;
  }
`;

const BenefitSubTitle = styled.span`
  color: ${COLORS.white};
  font-size: 14px;
  line-height: 16px;
  text-align: center;

  ${mediaQuery.uptoMobile} {
    line-height: 14px;
    font-size: 12px;
  }
`;

const CTAContainer = styled.div<{ $hasPriceImage?: boolean }>`
  padding: ${({ $hasPriceImage }) =>
    $hasPriceImage
      ? `${Spacings.SPACING_4B} ${Spacings.SPACING_4B} ${Spacings.SPACING_0B} ${Spacings.SPACING_4B}`
      : `${Spacings.SPACING_4B} ${Spacings.SPACING_4B} ${Spacings.SPACING_10} ${Spacings.SPACING_4B}`};

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const PrimaryCta = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  height: 48px;
  background: ${COLORS.purple};
  color: ${COLORS.white};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.1s ease;

  &:hover {
    filter: brightness(1.06);
  }
`;

const SecondaryLink = styled.button`
  border: none;
  background: none;
  color: ${COLORS.white};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  height: 48px;

  &:hover {
    text-decoration: underline;
  }
`;

export default function PrimeTrialPromoModal({
  open,
  onClose,
  onJoinPrime,
  heroVideoUrl,
  brandLogoSrc,
  primeLogoSrc,
  priceImageSrc,
  headline,
  thenLine,
  primaryCtaLabel,
  benefits
}: PrimeTrialPromoModalProps) {
  const isMobile = useIsMobile();

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      // @ts-expect-error MUI v4 Transition typing
      TransitionComponent={Transition}
    >
      <ModalSurface>
        {priceImageSrc && (
          <Hero>
            <Logo src={brandLogoSrc} alt="logo" width={22} height={18} />
            <VideoPreview src={heroVideoUrl} />
          </Hero>
        )}
        <div>
          <MidSection $hasPriceImage={Boolean(priceImageSrc)}>
            <Image
              src={primeLogoSrc}
              alt="prime logo"
              width={isMobile ? 110 : 147}
              height={isMobile ? 39 : 52}
              style={{ marginTop: '11px' }}
            />
            <OfferLine>{headline}</OfferLine>
            {priceImageSrc && (
              <Image
                src={priceImageSrc}
                alt="price image"
                width={isMobile ? 120 : 156}
                height={isMobile ? 120 : 156}
              />
            )}
            <ThenLine $hasPriceImage={Boolean(priceImageSrc)}>
              {thenLine
                .split(/%(.*?)%/)
                .map((part, index) =>
                  index % 2 === 1 ? (
                    <BoldThenLine key={index}>{part}</BoldThenLine>
                  ) : (
                    part
                  )
                )}
            </ThenLine>
          </MidSection>
          <BenefitsRow>
            {benefits.map((benefit) => (
              <BenefitCard key={benefit.title}>
                <Image
                  src={benefit.logoSrc}
                  alt={benefit.subtitle}
                  width={isMobile ? 34 : 40}
                  height={isMobile ? 34 : 40}
                />
                <BenefitTitle>{benefit.subtitle}</BenefitTitle>
                <BenefitSubTitle>{benefit.title}</BenefitSubTitle>
              </BenefitCard>
            ))}
          </BenefitsRow>
          <CTAContainer $hasPriceImage={Boolean(priceImageSrc)}>
            <PrimaryCta type="button" onClick={onJoinPrime}>
              {primaryCtaLabel}
            </PrimaryCta>
            <SecondaryLink type="button" onClick={onClose}>
              Maybe later
            </SecondaryLink>
          </CTAContainer>
        </div>
      </ModalSurface>
    </StyledDialog>
  );
}
